import React, { forwardRef } from 'react';

import { FormControlLabelProps, type CheckboxProps as NatterCheckboxProps, SxProps, Theme } from '@mui/material';
import { Checkbox as DsCheckbox } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { FormFieldHint } from '../FormFieldHint';
import { StyledFormControlLabel, StyledFormControlLabelProps } from './Checkbox.styled';
import { CheckboxControl } from './CheckboxControl';
import { toDsProps } from './toDsProps';

export type CheckboxProps = NatterCheckboxProps & {
  label?: React.ReactNode;
  invalid?: boolean;
  errorMessage?: string;
  labelSx?: SxProps<Theme>;
} & StyledFormControlLabelProps &
  Pick<FormControlLabelProps, 'labelPlacement'>;

/**
 * The visual-refresh gate lives HERE, around the label and the control together, because the DS
 * `Checkbox` IS a `<label>` and MUI's `FormControlLabel` is one too. Branching `CheckboxControl`
 * alone would nest the two.
 *
 * `toDsProps` returns `null` for anything DS 2.0 cannot express, which falls through to MUI.
 */
export const Checkbox = forwardRef<unknown, CheckboxProps>(
  (
    {
      checked,
      onChange,
      label,
      labelSx,
      variant = 'default',
      labelPlacement,
      labelAlign,
      inputProps,
      invalid,
      errorMessage,
      ...props
    },
    ref
  ) => {
    const dsProps = VISUAL_REFRESH
      ? toDsProps({ label, labelSx, variant, labelPlacement, labelAlign, inputProps, invalid, errorMessage, ...props })
      : null;

    if (dsProps) {
      const { attributes, ...rest } = dsProps;

      return (
        <DsCheckbox
          {...attributes}
          {...rest}
          ref={ref as React.Ref<HTMLElement>}
          checked={checked}
          // The DS event targets a `<span>`, not the `<input>` MUI handlers expect. No consumer reads it.
          onCheckedChange={(nextChecked, details) => onChange?.(details.event as never, nextChecked)}
        />
      );
    }

    return (
      <>
        <StyledFormControlLabel
          sx={labelSx}
          control=<CheckboxControl {...props} onChange={onChange} inputProps={inputProps} />
          // just in case someone passes empty string - it then makes it look broken
          label={label || undefined}
          labelPlacement={labelPlacement}
          labelAlign={labelAlign}
          variant={variant}
          checked={checked}
          ref={ref}
        />
        {invalid && errorMessage && <FormFieldHint error>{errorMessage}</FormFieldHint>}
      </>
    );
  }
);
